import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Snackbar
} from '@mui/material';
import timeEntryService, { TimeEntry } from '../services/timeEntryService';
import authService from '../services/authService';

const TimeTracking: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [currentTimeEntry, setCurrentTimeEntry] = useState<TimeEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const currentUser = authService.getCurrentUser();
  const currentUserRole = currentUser?.role || '';

  useEffect(() => {
    fetchTimeEntries();
  }, []);

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      const fetchedEntries = await timeEntryService.getAll();
      setTimeEntries(fetchedEntries);
      setError(null);
    } catch (err) {
      setError('Failed to fetch time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (entry: TimeEntry | null = null) => {
    if (entry) {
      // Employees can only edit their own entries
      if (currentUserRole === 'Employee' && entry.user_id !== currentUser?.id) {
        setSnackbar({ open: true, message: 'You can only edit your own time entries', severity: 'error' });
        return;
      }
    }
    setCurrentTimeEntry(entry);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentTimeEntry(null);
  };

  const handleSave = async () => {
    if (!currentTimeEntry || !currentUser) return;

    try {
      let savedEntry: TimeEntry;
      
      if (currentTimeEntry.id) {
        // Update existing entry
        // Employees can only update their own entries
        if (currentUserRole === 'Employee' && currentTimeEntry.user_id !== currentUser.id) {
          setSnackbar({ open: true, message: 'You can only update your own time entries', severity: 'error' });
          return;
        }
        
        savedEntry = await timeEntryService.update(currentTimeEntry.id, {
          ...currentTimeEntry,
          user_id: currentUserRole === 'Employee' ? currentUser.id : currentTimeEntry.user_id
        });
        setTimeEntries(timeEntries.map(e => e.id === savedEntry.id ? savedEntry : e));
        setSnackbar({ open: true, message: 'Time entry updated successfully', severity: 'success' });
      } else {
        // Create new entry
        savedEntry = await timeEntryService.create({
          ...currentTimeEntry,
          user_id: currentUserRole === 'Employee' ? currentUser.id : currentTimeEntry.user_id
        });
        setTimeEntries([...timeEntries, savedEntry]);
        setSnackbar({ open: true, message: 'Time entry created successfully', severity: 'success' });
      }
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save time entry', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    // Employees can only delete their own entries
    const entry = timeEntries.find(e => e.id === id);
    if (currentUserRole === 'Employee' && entry?.user_id !== currentUser?.id) {
      setSnackbar({ open: true, message: 'You can only delete your own time entries', severity: 'error' });
      return;
    }

    try {
      await timeEntryService.delete(id);
      setTimeEntries(timeEntries.filter(e => e.id !== id));
      setSnackbar({ open: true, message: 'Time entry deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete time entry', severity: 'error' });
    }
  };

  const handleChange = (field: keyof TimeEntry, value: string | number) => {
    if (currentTimeEntry) {
      setCurrentTimeEntry({ ...currentTimeEntry, [field]: value });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Time Tracking
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>
          Add Time Entry
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                {currentUserRole !== 'Employee' && <TableCell>User ID</TableCell>}
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Break Time (min)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeEntries.map((entry) => (
                <TableRow key={entry.id}>
                  {currentUserRole !== 'Employee' && <TableCell>{entry.user_id}</TableCell>}
                  <TableCell>{new Date(entry.start_time).toLocaleString()}</TableCell>
                  <TableCell>{new Date(entry.end_time).toLocaleString()}</TableCell>
                  <TableCell>{entry.break_time}</TableCell>
                  <TableCell>{entry.status}</TableCell>
                  <TableCell>
                    <Button onClick={() => handleOpen(entry)} size="small">Edit</Button>
                    <Button onClick={() => handleDelete(entry.id)} color="error" size="small">Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{currentTimeEntry?.id ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle>
        <DialogContent>
          {currentUserRole !== 'Employee' && (
            <TextField
              margin="dense"
              label="User ID"
              fullWidth
              type="number"
              value={currentTimeEntry?.user_id || currentUser?.id || ''}
              onChange={(e) => handleChange('user_id', parseInt(e.target.value))}
            />
          )}
          <TextField
            margin="dense"
            label="Start Time"
            fullWidth
            type="datetime-local"
            value={currentTimeEntry?.start_time ? new Date(currentTimeEntry.start_time).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleChange('start_time', e.target.value)}
          />
          <TextField
            margin="dense"
            label="End Time"
            fullWidth
            type="datetime-local"
            value={currentTimeEntry?.end_time ? new Date(currentTimeEntry.end_time).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleChange('end_time', e.target.value)}
          />
          <TextField
            margin="dense"
            label="Break Time (minutes)"
            fullWidth
            type="number"
            value={currentTimeEntry?.break_time || ''}
            onChange={(e) => handleChange('break_time', parseInt(e.target.value))}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={currentTimeEntry?.status || ''}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="Present">Present</MenuItem>
              <MenuItem value="Sick">Sick</MenuItem>
              <MenuItem value="PTO">PTO</MenuItem>
              <MenuItem value="Unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {currentTimeEntry?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TimeTracking;